// Import react.
import * as React from 'react';
// Import services.
import {
	DateService,
} from '@app/services';
// Import hooks.
import {
	useList,
	UseListResults,
} from '@app/hooks/view';
// Import enumerations.
import {
	AppThemeColor,
} from '@app/enumerations';
// Import material ui modules
import {
	Email,
	Flag,
	SpeakerNotesOutlined,
} from '@mui/icons-material';
// Import props.
import {
	SvgIconProps
} from '@mui/material';
// Import api models.
import {
	PolicyNoteEmail,
} from '@app/models/api';

interface UseListPolicyNoteEmailParameters {
	id: string;
	policyNoteEmails: PolicyNoteEmail[];
}

interface UseListPolicyNoteEmailResults extends UseListResults {
	isEmail: (policyNoteEmail: PolicyNoteEmail) => boolean;
	getDocName: (policyNoteEmail: PolicyNoteEmail) => string;
	getCorrespondenceName: (policyNoteEmail: PolicyNoteEmail) => string;
	getCorrespondencePhone1: (policyNoteEmail: PolicyNoteEmail) => string;
	getCorrespondencePhone2: (policyNoteEmail: PolicyNoteEmail) => string;
	getCorrespondenceRecvdOrSent: (policyNoteEmail: PolicyNoteEmail) => string;
	getCorrespondenceType: (policyNoteEmail: PolicyNoteEmail) => string;
	getCorrespondenceWho: (policyNoteEmail: PolicyNoteEmail) => string;
	getCorrespondenceSubject: (policyNoteEmail: PolicyNoteEmail) => string;
	getComplaintInitiatedText: (policyNoteEmail: PolicyNoteEmail) => string;
	getCorrespondenceEmailAddress: (policyNoteEmail: PolicyNoteEmail) => string;
	getCorrespondenceReasons: (policyNoteEmail: PolicyNoteEmail) => string;
	hasCorrespondenceInfo: (policyNoteEmail: PolicyNoteEmail) => boolean;
}

const useListPolicyNoteEmail = (params: UseListPolicyNoteEmailParameters): UseListPolicyNoteEmailResults => {

	// #region exported methods

	/**
	 * Checks to see if the note is an email.
	 * @param policyNoteEmail
	 */
	const isEmail = (policyNoteEmail: PolicyNoteEmail): boolean => {
		return policyNoteEmail.recordType === 'Email';
	}

	/**
	 * Fetches any existing doc name attached to the note.
	 * @param policyNoteEmail
	 */
	const getDocName = (policyNoteEmail: PolicyNoteEmail): string => {
		return policyNoteEmail.docName !== null
			? policyNoteEmail.docName
			: String.empty()
	}

	/**
	 * Fetches any existing correspondence name attached to the note.
	 * @param policyNoteEmail
	 */
	const getCorrespondenceName = (policyNoteEmail: PolicyNoteEmail): string => {
		return policyNoteEmail.correspondenceName !== null
			? policyNoteEmail.correspondenceName
			: String.empty();
	}

	/**
	 * Fetches any existing primary correspondence phone number attached to the note.
	 * @param policyNoteEmail
	 */
	const getCorrespondencePhone1 = (policyNoteEmail: PolicyNoteEmail): string => {
		return policyNoteEmail.correspondencePhone1 !== null
			? policyNoteEmail.correspondencePhone1
			: String.empty();
	}

	/**
	 * Fetches any existing secondary correspondence phone number attached to the note.
	 * @param policyNoteEmail
	 */
	const getCorrespondencePhone2 = (policyNoteEmail: PolicyNoteEmail): string => {
		return policyNoteEmail.correspondencePhone2 !== null
			? policyNoteEmail.correspondencePhone2
			: String.empty();
	}

	/**
	 * Fetches any existing correspondence-received-or-sent attached to the note.
	 * @param policyNoteEmail
	 */
	const getCorrespondenceRecvdOrSent = (policyNoteEmail: PolicyNoteEmail): string => {
		return policyNoteEmail.correspondenceRecvdOrSent !== null
			? policyNoteEmail.correspondenceRecvdOrSent
			: String.empty();
	}

	/**
	 * Fetches any existing correspondence-type attached to the note.
	 * @param policyNoteEmail
	 */
	const getCorrespondenceType = (policyNoteEmail: PolicyNoteEmail): string => {
		return policyNoteEmail.correspondenceType !== null
			? policyNoteEmail.correspondenceType
			: String.empty();
	}

	/**
	 * Fetches any existing correspondence-who attached to the note.
	 * @param policyNoteEmail
	 */
	const getCorrespondenceWho = (policyNoteEmail: PolicyNoteEmail): string => {
		return policyNoteEmail.correspondenceWho !== null
			? policyNoteEmail.correspondenceWho
			: String.empty();
	}

	/**
	 * Fetches any existing correspondence-subject attached to the note.
	 * @param policyNoteEmail
	 */
	const getCorrespondenceSubject = (policyNoteEmail: PolicyNoteEmail): string => {
		return policyNoteEmail.correspondenceSubject !== null
			? policyNoteEmail.correspondenceSubject
			: String.empty();
	}

	/**
	 * Fetches the text to indicate if a complaint was initiated.
	 * @param policyNoteEmail
	 */
	const getComplaintInitiatedText = (policyNoteEmail: PolicyNoteEmail): string => {
		if (policyNoteEmail.complaintInitiated !== null) {
			return policyNoteEmail.complaintInitiated
				? 'Yes'
				: 'No';
		}
		return String.empty();
	}

	/**
	 * Fetches any existing correspondence email attached to the note.
	 * @param policyNoteEmail
	 */
	const getCorrespondenceEmailAddress = (policyNoteEmail: PolicyNoteEmail): string => {
		return policyNoteEmail.correspondenceEmailAddress !== null
			? policyNoteEmail.correspondenceEmailAddress
			: String.empty();
	}

	/**
	 * Fetches any existing correspondence reason attached to the note.
	 * @param policyNoteEmail
	 */
	const getCorrespondenceReasons = (policyNoteEmail: PolicyNoteEmail): string => {
		return policyNoteEmail.correspondenceReason !== null
			? policyNoteEmail.correspondenceReason
			: String.empty();
	}

	/**
	 * Checks to see if the note has any correspondence info.
	 * @param policyNoteEmail
	 */
	const hasCorrespondenceInfo = (policyNoteEmail: PolicyNoteEmail): boolean => {
		return !String.isNullOrWhiteSpace(getCorrespondenceEmailAddress(policyNoteEmail))
			|| !String.isNullOrWhiteSpace(getCorrespondenceName(policyNoteEmail))
			|| !String.isNullOrWhiteSpace(getCorrespondencePhone1(policyNoteEmail))
			|| !String.isNullOrWhiteSpace(getCorrespondencePhone2(policyNoteEmail))
			|| !String.isNullOrWhiteSpace(getCorrespondenceReasons(policyNoteEmail))
			|| !String.isNullOrWhiteSpace(getCorrespondenceSubject(policyNoteEmail))
			|| !String.isNullOrWhiteSpace(getCorrespondenceType(policyNoteEmail))
			|| !String.isNullOrWhiteSpace(getCorrespondenceWho(policyNoteEmail));
	}

	// #endregion exported methods


	// #region private methods

	/**
	 * Fetches the username of the note.
	 * @param policyNoteEmail
	 */
	const getUserName = (policyNoteEmail: PolicyNoteEmail): string => {
		return policyNoteEmail.username !== null
			? policyNoteEmail.username
			: String.empty();
	}

	/**
	 * Fetches the admin only text display.
	 * @param policyNoteEmail
	 */
	const getAdminOnlyText = (policyNoteEmail: PolicyNoteEmail): string => {
		return policyNoteEmail.adminOnly !== null && policyNoteEmail.adminOnly
			? '(Admin Only)'
			: String.empty();
	}

	/**
	 * Checks to see if the note is flagged.
	 * @param policyNoteEmail
	 */
	const isFlaggedPolicyNote = (policyNoteEmail: PolicyNoteEmail): boolean => {
		return policyNoteEmail !== undefined
			&& policyNoteEmail.flagDate !== null;
	}

	/**
	 * Fetches the flag date as text.
	 * @param policyNoteEmail
	 */
	const getFlagDateText = (policyNoteEmail: PolicyNoteEmail): string => {
		return policyNoteEmail.flagDate !== null
			? DateService.getInstance().formatDate(policyNoteEmail.flagDate)
			: String.empty();
	}

	/**
	 * Fetches the submission date of the policy note email record.
	 * @param policyNoteEmail
	 */
	const getSubmitDateText = (policyNoteEmail: PolicyNoteEmail): string => {
		return policyNoteEmail.dateSubmit !== null
			? DateService.getInstance().formatDate(policyNoteEmail.dateSubmit)
			: String.empty();
	}

	/**
	 * Fetches the matching policy note email based on the policy note email id.
	 * @param policyNoteEmailId
	 */
	const getPolicyNoteEmailListItemRecord = (policyNoteEmailId: string): PolicyNoteEmail | undefined => {
		return params.policyNoteEmails.find((policyNoteEmail: PolicyNoteEmail) => policyNoteEmail.id !== null && policyNoteEmail.id.toString() === policyNoteEmailId);
	}

	/**
	 *  Fetches the title of the policy note email list item.
	 *  @param policyNoteEmail
	 */
	const getHeaderTitle = (policyNoteEmail: PolicyNoteEmail, index: number): string => {
		let submitDate: string = getSubmitDateText(policyNoteEmail);
		let submittedBy: string = getUserName(policyNoteEmail);
		let adminOnlyText: string = getAdminOnlyText(policyNoteEmail);

		let preTextTitle: string = [submitDate, submittedBy]
			.filter((titleToken: string) => !String.isNullOrWhiteSpace(titleToken))
			.join(' - ');

		return [preTextTitle, adminOnlyText]
			.filter((titleToken: string) => !String.isNullOrWhiteSpace(titleToken))
			.join(' ');
	}

	/**
	 * Fetches the prefix header title icon.
	 * @param policyNoteEmail
	 */
	const getHeaderTitleIcon = (policyNoteEmail: PolicyNoteEmail): React.ComponentType<SvgIconProps> => {
		return isEmail(policyNoteEmail)
			? Email
			: SpeakerNotesOutlined
	}

	/**
	 *  Fetches the sub-title of the policy note email list item.
	 */
	const getSubtitle = (policyNoteEmail: PolicyNoteEmail): string | undefined => {
		if (isFlaggedPolicyNote(policyNoteEmail)) {
			return `Flag Date: ${getFlagDateText(policyNoteEmail)}`;
		}
		return undefined;
	}

	/**
	 *  Fetches the sub-title icon.
	 */
	const getSubtitleIcon = (policyNoteEmail: PolicyNoteEmail): React.ComponentType<SvgIconProps> | undefined => {
		return isFlaggedPolicyNote(policyNoteEmail)
			? Flag
			: undefined;
	}

	/**
	 *  Fetches the mobile sub-title icon.
	 */
	const getMobileIcon = (policyNoteEmail: PolicyNoteEmail): React.ComponentType<SvgIconProps> | undefined => {
		return isEmail(policyNoteEmail)
			? Email
			: SpeakerNotesOutlined
	}

	/**
	 * Fetches the list item type based off the policy note email completion status.
	 * @param policyNoteEmail
	 */
	const getListItemType = (policyNoteEmail: PolicyNoteEmail): AppThemeColor => {
		return AppThemeColor.PRIMARY;
	}

	// #region id generators

	const generateIdForPolicyNoteEmailListItemElement = (policyNoteEmail: PolicyNoteEmail): string => {
		return `${params.id}-policyNoteEmailListItem-${policyNoteEmail.id}`;
	}

	// #endregion id generators

	// #endregion private methods


	// #region variables

	// Setup the 'list' hook.
	const list: UseListResults = useList<PolicyNoteEmail>({
		getListItemRecord: getPolicyNoteEmailListItemRecord,
		generateListItemElementId: generateIdForPolicyNoteEmailListItemElement,
		getHeaderTitle: getHeaderTitle,
		getHeaderTitleIcon: getHeaderTitleIcon,
		getSubtitle: getSubtitle,
		getSubtitleIcon: getSubtitleIcon,
		getMobileIcon: getMobileIcon,
		getListItemType: getListItemType,
		isCollapsible: true,
		isInitiallyCollapsed: true,
	});

	// #endregion variables


	return {
		...list,
		isEmail: isEmail,
		getDocName: getDocName,
		getCorrespondenceName: getCorrespondenceName,
		getCorrespondencePhone1: getCorrespondencePhone1,
		getCorrespondencePhone2: getCorrespondencePhone2,
		getCorrespondenceRecvdOrSent: getCorrespondenceRecvdOrSent,
		getCorrespondenceType: getCorrespondenceType,
		getCorrespondenceWho: getCorrespondenceWho,
		getCorrespondenceSubject: getCorrespondenceSubject,
		getComplaintInitiatedText: getComplaintInitiatedText,
		getCorrespondenceEmailAddress: getCorrespondenceEmailAddress,
		getCorrespondenceReasons: getCorrespondenceReasons,
		hasCorrespondenceInfo: hasCorrespondenceInfo,
	};
}

// Export parameters.
export {
	UseListPolicyNoteEmailParameters,
}

// Export results.
export {
	UseListPolicyNoteEmailResults,
}

export default useListPolicyNoteEmail;